import React, { useState, useEffect } from 'react';
import { Search, ExternalLink, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { loadOrdinalData, lookupOrdinalId, getOrdinalUrl } from '../utils/ordinalLookup';

export default function SearchBar() {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await loadOrdinalData();
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to initialize ordinal data:', err);
        setError(t('errors.dataLoad'));
        setIsLoading(false);
      }
    };

    initializeData();
  }, [t]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const numericValue = parseInt(value, 10);
    
    if (value === '' || (numericValue >= 1 && numericValue <= 10000)) {
      setSearchValue(value);
      setError(null);
    }
  };

  const handleViewOrdinal = () => {
    if (!searchValue) return;
    
    try {
      const numericValue = parseInt(searchValue, 10);
      if (isNaN(numericValue) || numericValue < 1 || numericValue > 10000) {
        setError(t('errors.rockNotFound'));
        return;
      }

      const ordinalId = lookupOrdinalId(searchValue);
      if (!ordinalId) {
        setError(t('errors.rockNotFound'));
        return;
      }

      const url = getOrdinalUrl(ordinalId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open ordinal:', err);
      setError(t('errors.openFailed'));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleViewOrdinal();
    }
  };

  return (
    <div className="relative max-w-xl sm:max-w-2xl lg:max-w-3xl mx-auto mt-6 sm:mt-8 lg:mt-12 group">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-primary-light 
        rounded-xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
      
      <div className="relative flex items-center">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={t('landing.searchPlaceholder')}
          value={searchValue}
          onChange={handleSearch}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          className={`w-full h-12 sm:h-14 px-4 sm:px-6 bg-dark-lighter rounded-l-xl 
            border border-dark-light focus:border-primary/50 outline-none 
            transition-all duration-300 pl-12 sm:pl-14 text-base sm:text-lg
            group-hover:shadow-[0_0_30px_rgba(255,85,0,0.1)]
            ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-no-float="true"
        />
        
        {isLoading ? (
          <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6
            text-primary/50 animate-spin" />
        ) : (
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6
            text-primary/50 group-hover:text-primary transition-colors duration-300" />
        )}

        <button
          onClick={handleViewOrdinal}
          disabled={!searchValue || isLoading}
          className={`h-12 sm:h-14 px-4 sm:px-6 bg-dark-lighter rounded-r-xl 
            border border-l-0 border-dark-light hover:border-primary/50 
            transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
            ${error ? 'border-red-500/50' : ''}`}
          data-no-float="true"
        >
          <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 text-primary/50 
            group-hover:text-primary transition-colors duration-300" />
        </button>
      </div>

      {error && (
        <div className="absolute left-0 right-0 top-full mt-2 px-4 py-2 
          bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}